import { useEffect, useRef, type ReactNode } from 'react';

import type { Item } from '../api/types.js';

/**
 * The item reader (M1.16.2).
 *
 * ## The markup is the server's, sanitised before it was stored
 *
 * Feed content is sanitised at ingest (M1.2.7) — allowlisted elements and
 * attributes, no scripts, no event handlers, no `javascript:` URLs — so what
 * arrives here has already been through it. Sanitising again in the browser
 * would be a second implementation of a security boundary, which is how the
 * two drift apart and one of them becomes the weaker one everybody forgets.
 *
 * `dangerouslySetInnerHTML` is therefore deliberate and narrow: it renders
 * *this* field and nothing else. The **archive** — genuinely untrusted markup
 * rendered from a page we did not sanitise — is served from a separate origin
 * by [D4](../../../Design-Decisions.md) and arrives in M3.12; it must never be
 * inlined here, and the placeholder below is where it lands.
 *
 * ## Links leave
 *
 * `target="_blank"` with `rel="noopener noreferrer"`. Without `noopener` the
 * opened page gets a handle on this one through `window.opener`; the
 * sanitiser adds the attribute server-side too, and this is the second half of
 * the same rule for links we render ourselves.
 */

export interface ItemViewProps {
  readonly item: Item | undefined;
  readonly onToggleRead: (read: boolean) => void;
  readonly onOpen: () => void;
  /** So `space` can ask whether there is more article to scroll. */
  readonly scrollRef: React.RefObject<HTMLDivElement | null>;
}

export function ItemView(props: ItemViewProps): ReactNode {
  const { item } = props;
  const lastMarked = useRef<string | undefined>(undefined);

  /*
   * Marks read on open (M1.16.2), once per item.
   *
   * Guarded by the id rather than by a boolean: without it, marking an item
   * *unread* while it is open would immediately mark it read again, and the
   * explicit control would appear not to work.
   */
  useEffect(() => {
    if (item === undefined || item.read || lastMarked.current === item.id) {
      return;
    }
    lastMarked.current = item.id;
    props.onToggleRead(true);
  }, [item, props]);

  if (item === undefined) {
    return (
      <article className="reader reader--empty">
        <p className="state__text">Select an item to read it.</p>
      </article>
    );
  }

  return (
    <article className="reader" ref={props.scrollRef} tabIndex={-1}>
      <header className="reader__header">
        <h1 className="reader__title">
          {item.link === null ? (
            item.title
          ) : (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={props.onOpen}
            >
              {item.title}
            </a>
          )}
        </h1>
        <p className="reader__meta">
          {item.feedTitle ?? 'Unknown feed'}
          {item.author === null ? '' : ` · ${item.author}`}
          {' · '}
          <time dateTime={item.publishedAt}>{formatDate(item.publishedAt)}</time>
        </p>
        <div className="reader__actions">
          <button
            className="button button--small"
            onClick={() => {
              props.onToggleRead(!item.read);
            }}
          >
            {item.read ? 'Mark unread' : 'Mark read'}
          </button>
          {item.link !== null && (
            <a
              className="button button--small"
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={props.onOpen}
            >
              Open original
            </a>
          )}
        </div>
      </header>

      {item.summaryHtml === null || item.summaryHtml === '' ? (
        <p className="reader__empty-body">
          This feed publishes titles only. Open the original to read it.
        </p>
      ) : (
        <div
          className="reader__body"
          // Sanitised server-side at ingest (M1.2.7). See the note above.
          dangerouslySetInnerHTML={{ __html: item.summaryHtml }}
        />
      )}

      {/*
       * Where the archived article lands (M3.12). A placeholder rather than
       * nothing, because "archive" is the paid feature and a tester should be
       * able to see where it will appear — and because `extraction` already
       * travels on every item.
       */}
      <footer className="reader__archive">
        <p className="state__meta">
          {item.extraction === 'ok'
            ? 'A full archived copy exists. Reading it here arrives with the archive (M3.12).'
            : 'Full-text archiving arrives with the archive (M3).'}
        </p>
      </footer>
    </article>
  );
}

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};
